import { Service } from "typedi";
import { BlockOverlayData } from "../../../shared/ChannelProtocol";
import { WebCategoryRuleValueStoreEvaluationResult } from "../../../../../schools-backend/src/rule/application/stores/ruleValueStores/WebCategoryRuleValueStore";
import { RuleTypeEnum } from "../../../../../schools-backend/src/rule/domain/RuleType";
import { StudentRulesCache } from "./StudentRulesCache";

export interface EvaluationResult {
    action: "allow" | "block";
    data?: BlockOverlayData;
}

interface WebCategoryCacheEntry {
    result: WebCategoryRuleValueStoreEvaluationResult;
    expiresAt: number;
}

const WEB_CATEGORY_TTL_MS = 5 * 60 * 1000;

type CategoryLookup = (url: string) => Promise<WebCategoryRuleValueStoreEvaluationResult>;

/**
 * Decides allow/block for a URL, in the spirit of the chrome agent's
 * `StudentRulesService.isUrlBlocked`.
 *
 * Three layers (first ALLOWED/DENIED wins, NO_MATCH falls through):
 *   1. WEB_URL rule value store (exact / startsWith URL match) — sync.
 *   2. WEB_HOSTNAME rule value store (hostname + subdomains)   — sync.
 *   3. WEB_CATEGORY rule value store (server categorizes)      — async, 5m cache.
 */
@Service()
export class UrlEvaluator {
    private readonly categoryCache = new Map<string, WebCategoryCacheEntry>();
    private categoryLookup: CategoryLookup | null = null;

    constructor(private readonly rulesCache: StudentRulesCache) {}

    /** Leader wires this with `agentServerAPI.queryUrlEvaluationByWebCategory.bind(api)`. */
    setCategoryLookup(lookup: CategoryLookup | null): void {
        this.categoryLookup = lookup;
    }

    async evaluate(urlString: string): Promise<EvaluationResult> {
        let url: URL;
        try {
            url = new URL(urlString);
        } catch {
            return { action: "allow" };
        }
        if (url.protocol !== "http:" && url.protocol !== "https:") {
            return { action: "allow" };
        }

        const rules = this.rulesCache.getHydrated();

        if (rules) {
            const webUrlResult = rules.rules[RuleTypeEnum.WEB_URL].evaluateWebUrl(url);
            if (webUrlResult === "ALLOWED") return { action: "allow" };
            if (webUrlResult === "DENIED") return this.block("web-url");

            const webHostnameResult = rules.rules[RuleTypeEnum.WEB_HOSTNAME].evaluateWebHostname(url.hostname);
            if (webHostnameResult === "ALLOWED") return { action: "allow" };
            if (webHostnameResult === "DENIED") return this.block("web-hostname");
        }

        const categoryResult = await this.evaluateByCategory(url.href);
        if (categoryResult === "ALLOWED") return { action: "allow" };
        if (categoryResult === "DENIED") return this.block("web-category");

        return { action: "allow" };
    }

    private block(category: "web-url" | "web-hostname" | "web-category"): EvaluationResult {
        return {
            action: "block",
            data: {
                category,
                title: "Acceso bloqueado",
                message: "Este sitio está bloqueado por tu centro educativo."
            }
        };
    }

    invalidate(urlString: string, result?: WebCategoryRuleValueStoreEvaluationResult): void {
        this.categoryCache.delete(urlString);
        if (result) {
            this.categoryCache.set(urlString, {
                result,
                expiresAt: Date.now() + WEB_CATEGORY_TTL_MS
            });
        }
    }

    clear(): void {
        this.categoryCache.clear();
    }

    private async evaluateByCategory(url: string): Promise<WebCategoryRuleValueStoreEvaluationResult> {
        const entry = this.categoryCache.get(url);
        if (entry && entry.expiresAt > Date.now()) {
            return entry.result;
        }

        if (!this.categoryLookup) {
            return "NO_MATCH";
        }

        try {
            const result = await this.categoryLookup(url);
            this.categoryCache.set(url, { result, expiresAt: Date.now() + WEB_CATEGORY_TTL_MS });
            // Side effect: trigger a "watched URLs" log if rules present. Done
            // by caller — UrlEvaluator stays pure evaluation.
            void this.rulesCache.get();
            return result;
        } catch (err) {
            console.warn("[url-evaluator] category lookup failed", err);
            return "NO_MATCH";
        }
    }
}
